import { forwardRef, useImperativeHandle } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as LinkIcon, LinkOff } from '@mui/icons-material';
import { CapabilityUnavailableOverlay } from '@shared/ui/CapabilityUnavailableOverlay';
import { useCompanyDatacenters } from '../application/useCompanyDatacenters';
import AddDatacenterAssociationDialog from './AddDatacenterAssociationDialog';
import { datacenterKindColor, formatDatacenterLocation } from '../domain/clientCompany.rules';
import type { Id } from '../domain/enterprise.types';
import type { ClientCompany } from '../domain/clientCompany.types';

export interface CompanyDatacentersDialogHandle {
  open: (company: ClientCompany) => void;
}

export interface CompanyDatacentersDialogProps {
  clientId: Id;
  canManage: boolean;
  onAssociationChanged: () => void;
}

// UC-11 (decisión E: literales españoles as-is; decisión H: desvincular vía `window.confirm`
// nativo dentro de `useCompanyDatacenters`, no en este componente). Posee `useCompanyDatacenters`;
// el contenedor lo abre imperativamente vía ref (open(company)) desde el ícono "Datacenters
// asociados" de `ClientCompaniesTable` — que NO está gateado por `canManage` (lectura abierta).
const CompanyDatacentersDialog = forwardRef<
  CompanyDatacentersDialogHandle,
  CompanyDatacentersDialogProps
>(function CompanyDatacentersDialog({ clientId, canManage, onAssociationChanged }, ref) {
  const {
    company,
    associations,
    loading,
    open,
    close,
    addOpen,
    addForm,
    setAddKind,
    setAddField,
    kyndrylOptions,
    clientOptions,
    openAdd,
    closeAdd,
    saveAssociation,
    saving,
  } = useCompanyDatacenters(clientId, onAssociationChanged);

  useImperativeHandle(ref, () => ({ open }), [open]);

  return (
    <>
      <Dialog open={!!company} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle>Datacenters asociados a {company?.name}</DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {canManage && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LinkIcon />}
                    onClick={openAdd}
                  >
                    Asociar datacenter
                  </Button>
                </Box>
              )}

              {associations.length === 0 ? (
                <Alert severity="info">
                  Esta empresa no tiene datacenters asociados.
                  {canManage && ' Usa el botón para asociar uno.'}
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Datacenter</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Ubicación</TableCell>
                        <TableCell>Notas</TableCell>
                        {canManage && <TableCell align="right">Acción</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {associations.map((a) => (
                        <TableRow key={a.association_id} hover>
                          <TableCell>
                            <Typography fontWeight={500}>{a.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={a.kind === 'kyndryl' ? 'Kyndryl' : 'Propio del cliente'}
                              color={datacenterKindColor(a.kind)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{formatDatacenterLocation(a.city, a.country_name)}</TableCell>
                          <TableCell>{a.notes || '—'}</TableCell>
                          {canManage && (
                            <TableCell align="right">
                              <CapabilityUnavailableOverlay>
                                <Tooltip title="Desvincular">
                                  <IconButton size="small" color="error">
                                    <LinkOff fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </CapabilityUnavailableOverlay>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <AddDatacenterAssociationDialog
        open={addOpen}
        kind={addForm.kind}
        datacenterId={addForm.datacenter_id}
        clientDatacenterId={addForm.client_datacenter_id}
        notes={addForm.notes}
        kyndrylOptions={kyndrylOptions}
        clientOptions={clientOptions}
        onKindChange={setAddKind}
        onDatacenterIdChange={(v) => setAddField('datacenter_id', v)}
        onClientDatacenterIdChange={(v) => setAddField('client_datacenter_id', v)}
        onNotesChange={(v) => setAddField('notes', v)}
        onClose={closeAdd}
        onSave={saveAssociation}
        saving={saving}
      />
    </>
  );
});

export default CompanyDatacentersDialog;
